import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  type FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { firstValueFrom, take } from 'rxjs';

import { noWhitespaceValidator } from '@core/common-legacy';
import { AuthenticationDialogComponent } from '@core/components-authorization-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import { getDefaultTimezone } from '@core/time-legacy';
import {
  DialogComponentI,
  FormFieldComponent,
  InputComponent,
  LabelComponent,
  ProtectedControlDirective,
  ButtonComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DialogService,
  DialogWidth,
  ButtonLoadingDirective,
} from '@core/user-interface-legacy';

type DialogData = { recoveryEmail: string; timezone: string };
type DialogResult = { success: true; payload: { newRecoveryEmail: string } } | undefined;

interface PasswordRecoveryEmailForm {
  recoveryEmail: FormControl<string>;
}

@Component({
  selector: 'rt-password-recovery-email-dialog',
  templateUrl: './password-recovery-email-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    ButtonComponent,
    TranslatePipe,
    FormFieldComponent,
    FormsModule,
    InputComponent,
    LabelComponent,
    ProtectedControlDirective,
    ReactiveFormsModule,
    ButtonLoadingDirective,
  ],
})
export class PasswordRecoveryEmailDialogComponent implements DialogComponentI<
  DialogData,
  DialogResult
> {
  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);
  private readonly dialogRef = inject(DialogRef);
  private readonly dialogService = inject(DialogService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly vcRef = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);

  protected loading = signal<boolean>(false);
  protected recoveryEmail = signal<string>('');
  protected timezone = signal<string | null>(null);
  protected form = this.formBuilder.nonNullable.group<PasswordRecoveryEmailForm>({
    recoveryEmail: new FormControl<string>('', {
      validators: [Validators.email, Validators.required, noWhitespaceValidator],
      nonNullable: true,
    }),
  });

  public setDialogData(data: DialogData, triggerUpdate: boolean): void {
    this.recoveryEmail.set(data.recoveryEmail);
    this.timezone.set(data.timezone);
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected async submit() {
    this.loading.set(true);

    // if user doesn't have recovery email -> we don't want to send secure code
    if (this.recoveryEmail()) {
      const { afterClosed } = this.dialogService.openCustom(
        AuthenticationDialogComponent,
        {
          width: DialogWidth.SmallX,
        },
        {},
        this.vcRef,
      );
      const emailCode = await firstValueFrom(afterClosed);

      if (emailCode) {
        this.dispatchFormUpdate(this.form, emailCode);
      }
      return;
    }

    this.dispatchFormUpdate(this.form);
  }

  private dispatchFormUpdate(form: FormGroup<PasswordRecoveryEmailForm>, code?: string): void {
    const newRecoveryEmail = form.value.recoveryEmail;

    // Members migrated from Javelo/Grow might have `timezone` field `null` hence we need to set this manually here.
    this.currentUserFacade.updateUser({
      ...form.value,
      code,
      timezone: this.timezone() ?? getDefaultTimezone(),
    });

    this.currentUserFacade.updateUserSuccess$
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(action => {
        this.form.markAsPristine();
        this.loading.set(false);
        this.dialogRef.close({
          success: true,
          payload: { newRecoveryEmail: newRecoveryEmail },
        });
      });

    this.currentUserFacade.updateUserFail$
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(error => {
        const recoveryEmailError = error.payload.error.errors.recoveryEmail[0];
        console.error(recoveryEmailError);
        this.loading.set(false);
      });
  }
}
